import React from 'react'
import { twMerge } from 'tailwind-merge'

import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { type FilterSwitchProps } from './FilterSwitch.types'

/**
 * Row of pill toggles (Figma Filter `6868:70896` / FilterSwitch `6868:70945`):
 * the selected option renders as a filled, hairline-bordered rounded pill with
 * default-ink text; the others are muted, transparent, content-sized text.
 * Unlike `SegmentedControl` options hug their label. Pass `track` to wrap the
 * group in the Figma elevation-track pill (AOS-842 weather footers); without it
 * the plain untracked variant renders, as before (AOS-636).
 */
export function FilterSwitch<T extends string | number>({
    options,
    value,
    onChange,
    track = false,
    className,
    testID,
}: FilterSwitchProps<T>) {
    return (
        <HStack
            accessibilityRole='tablist'
            testID={testID}
            className={twMerge(
                'flex-row items-center gap-1',
                track && 'self-start rounded-full bg-aos-bg-elevation p-1',
                className,
            )}
        >
            {options.map(option => {
                const isSelected = option.value === value
                // Tracked: selected pill lifts to the surface colour with a strong
                // border. Untracked: the original elevation-filled subtle-border pill.
                // The selected pill hovers too (AOS-844) — previously only the
                // unselected ones had an affordance.
                const selectedPillClass = track
                    ? 'bg-aos-bg-surface border border-solid border-border-strong web:hover:bg-aos-bg-surface-hover'
                    : 'bg-aos-bg-elevation border border-solid border-border-subtle web:hover:bg-aos-bg-elevation-hover'
                // Tracked, the group itself is already elevation, so an unselected
                // pill has to hover one step further to be visible at all.
                const unselectedPillClass = track
                    ? 'bg-transparent border border-solid border-transparent web:hover:bg-aos-bg-elevation-hover'
                    : 'bg-transparent border border-solid border-transparent web:hover:bg-aos-bg-elevation'
                return (
                    <Pressable
                        key={String(option.value)}
                        onPress={() => onChange(option.value)}
                        accessibilityRole='tab'
                        accessibilityState={{ selected: isSelected }}
                        className={twMerge(
                            track ? 'rounded-full px-3 py-1' : 'rounded-full px-3 py-1.5',
                            'web:cursor-pointer web:hover:border-border-strong',
                            isSelected ? selectedPillClass : unselectedPillClass,
                        )}
                    >
                        <Text
                            size='caption'
                            className={twMerge(
                                'text-center',
                                isSelected ? 'text-aos-text-default' : 'text-aos-text-muted',
                            )}
                        >
                            {option.label}
                        </Text>
                    </Pressable>
                )
            })}
        </HStack>
    )
}
